import { ChangeEvent, useState } from "react";
import {
  CheckIcon,
  EditIcon,
  FileEditAltIcon,
  IconButton,
  Paragraph,
  RichText,
  RichTextDisplay,
  TextInput,
  ToggleInput,
} from "@datagame/ui";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { ATTRIBUTE_UPDATE } from "../../../../../../../graphql/mutations/AttributeMutation";
import {
  type Attribute,
  type AttributeUpdateInput,
  type CustomRatingQuestion,
  type Game,
  type LibraryImage,
  LibraryImageKind,
} from "../../../../../../../gql/graphql";
import { isLeaderboardGame } from "../../../../../../../graphql/helpers/LeaderboardGame";
import { isWordopolisGame } from "../../../../../../../graphql/helpers/isWordopolisGame";
import { PointsSection } from "./PointsSection";
import { ScaleQuestionSectionTemplate } from "../../../../../../shared/ScaleQuestionSectionTemplate";
import { ImageInput, Section } from "../../Common";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";
import { isSwiperGame } from "../../../../../../../graphql/helpers/SwiperGame";
import { useProject } from "../../../../../../../contexts/project/ProjectContext";

import styles from "./AttributeItem.module.scss";

const cx = classNames.bind(styles);

export interface AttributeItemProps {
  attribute: Attribute;
  isWordopolis?: boolean;
  game: Game | null;
}

export const AttributeItem = ({ game, attribute, isWordopolis }: AttributeItemProps) => {
  const { t } = useTranslation();
  const { project } = useProject();
  const [isEditing, setIsEditing] = useState(false);
  const [isUsingImage, setIsUsingImage] = useState(Boolean(attribute.image));
  const [isShowingImageAndText, setIsShowingImageAndText] = useState(attribute.showImageAndText);
  const [customText, setCustomText] = useState(attribute.customText);
  const [cardFrontImage, setCardFrontImage] = useState(attribute.image);
  const [isCorrect, setIsCorrect] = useState(attribute.isCorrect);
  const [points, setPoints] = useState(attribute.points);
  const [customRatingQuestion, setCustomRatingQuestion] = useState<CustomRatingQuestion | undefined | null>(
    attribute.customRatingQuestion,
  );
  const isLeaderboardEnabled =
    (isLeaderboardGame(game) && game.configuration.showLeaderboard) ||
    (isLeaderboardGame(game) && project?.showLeaderboard);
  const isShowCorrectnessEnabled = isSwiperGame(game) && game.configuration.showSwipeCorrectness;

  const [updateAttribute] = useMutation(ATTRIBUTE_UPDATE);

  const handleCustomTextChange = (value: string) => {
    setCustomText(value);
  };

  const handleWordopolisCustomTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCustomText(event.target.value);
  };

  const handleUpdate = (data: AttributeUpdateInput) => {
    updateAttribute({
      variables: {
        attributeId: attribute.id,
        data,
      },
    });
  };

  const handleChangePoints = (value: number) => {
    setPoints(value);

    handleUpdate({
      points: value,
    });
  };

  const handleSetIsCorrect = (value: boolean) => {
    setIsCorrect(value);

    handleUpdate({
      isCorrect: value,
    });
  };

  const handleSaveRatingQuestion = (config: CustomRatingQuestion) => {
    setCustomRatingQuestion(config);

    handleUpdate({
      customRatingQuestion: config,
    });
  };

  const toggleIsEditing = () => {
    setIsEditing(!isEditing);
  };

  const handleSetShowImageAndText = (value: boolean) => {
    setIsShowingImageAndText(value);

    handleUpdate({
      showImageAndText: value,
    });
  };

  const handleSetIsUsingImage = (value: boolean) => {
    setIsUsingImage(value);

    if (!value) {
      setCardFrontImage(null);
      handleUpdate({
        imageId: null,
      });
    }
  };

  const handleUploadFrontImage = (image: LibraryImage) => {
    setCardFrontImage(image);

    handleUpdate({
      imageId: image.id,
    });
  };

  const handleSaveCustomText = () => {
    handleUpdate({
      customText,
    });
  };

  const cardText = isWordopolis ? t("project:advancedSettings.wordText") : t("project:advancedSettings.cardText");
  const cardTextTooltip = isWordopolis
    ? t("project:advancedSettings.wordTextTooltip")
    : t("project:advancedSettings.cardTextTooltip");

  const renderActionButton = () => {
    if (isEditing) {
      return <IconButton icon={<CheckIcon />} style="outline" onClick={toggleIsEditing} />;
    }

    return <IconButton icon={<EditIcon />} className={styles.editIcon} onClick={() => setIsEditing(true)} />;
  };

  const imageSection = (
    <Section title={t("project:advancedSettings.cardFrontImage")} className={cx("withoutMargin")}>
      <ImageInput image={cardFrontImage} onUpload={handleUploadFrontImage} kind={LibraryImageKind.CardFront} />
    </Section>
  );
  const textSection = (
    <Section title={cardText} className={cx("withoutMargin")} tooltip={<TooltipWithIcon content={cardTextTooltip} />}>
      {isWordopolis ? (
        <TextInput
          placeholder="Some replaced text"
          size="small"
          icon={<FileEditAltIcon />}
          value={customText ?? ""}
          onChange={handleWordopolisCustomTextChange}
          onBlur={handleSaveCustomText}
        />
      ) : (
        <RichText
          placeholder="Some replaced text"
          size="small"
          icon={<FileEditAltIcon />}
          value={customText ?? ""}
          onChange={handleCustomTextChange}
          onBlur={handleSaveCustomText}
        />
      )}
    </Section>
  );

  const renderEditSection = () => {
    if (isShowingImageAndText) {
      return (
        <>
          {isUsingImage && imageSection}
          {textSection}
        </>
      );
    }

    if (isUsingImage) {
      return imageSection;
    }

    return textSection;
  };

  const title = isWordopolis ? t("project:advancedSettings.word") : t("project:advancedSettings.attribute");

  return (
    <div className={cx("attribute", { isEditing })}>
      <div className={styles.nameWrapper}>
        <div className={styles.attributeName}>
          <div className={styles.title}>{title}</div>
          {isWordopolis ? (
            <Paragraph size="small" weight="font-bold" color="secondary" className={styles.name}>
              {attribute.name}
            </Paragraph>
          ) : (
            <RichTextDisplay value={attribute?.name} className={styles.name} />
          )}
        </div>

        {renderActionButton()}
      </div>

      {isEditing && (
        <>
          {renderEditSection()}
          {!isWordopolis && (
            <>
              <ToggleInput
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSetIsUsingImage(e.target.checked)}
                value={isUsingImage}
                label={t("project:advancedSettings.transferToImage") ?? ""}
                name="transfer"
                tooltip={<TooltipWithIcon content={t("project:advancedSettings.transferToImageTooltip")} />}
              />
              {isUsingImage && (
                <ToggleInput
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSetShowImageAndText(e.target.checked)}
                  value={isShowingImageAndText}
                  label={t("project:advancedSettings.showImageAndText") ?? ""}
                  name="show-image-and-text"
                  tooltip={<TooltipWithIcon content={t("project:advancedSettings.showImageAndTextTooltip")} />}
                />
              )}
            </>
          )}

          {(isLeaderboardEnabled || isShowCorrectnessEnabled) && (
            <PointsSection
              game={game}
              points={points}
              showPoints={isLeaderboardEnabled}
              isCorrect={isCorrect}
              onUpdatePoints={handleChangePoints}
              onToggleCorrect={handleSetIsCorrect}
            />
          )}

          {game && isWordopolisGame(game) && game.configuration.showRatingScale ? (
            <>
              <ToggleInput
                className={styles.customRatingQuestionToggle}
                label="Custom rating question"
                name="customRatingQuestion"
                tooltip={<TooltipWithIcon content={t("project:advancedSettings.customRatingQuestionTooltip")} />}
                value={Boolean(customRatingQuestion)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const isChecked = e.target.checked;

                  if (isChecked) {
                    setCustomRatingQuestion({
                      question: "How much do you like or dislike this word?",
                      leftLabel: "I hate it!",
                      rightLabel: "I love it!",
                      scaleSize: 10,
                    });
                  } else {
                    setCustomRatingQuestion(null);
                  }
                }}
              />
              {customRatingQuestion && (
                <ScaleQuestionSectionTemplate onChange={handleSaveRatingQuestion} value={customRatingQuestion} />
              )}
            </>
          ) : null}
        </>
      )}
    </div>
  );
};
